import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useChatStream } from "@/lib/useChatStream";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Send, Square, Plus, Search, Sparkles, Bot, Trash2, Pencil, MessageSquare } from "lucide-react";

const STARTER_PROMPTS = [
  { emoji: "◆", title: "Smart money in NVDA", prompt: "What's the smart money doing in NVDA this week?" },
  { emoji: "◆", title: "BTC 3-month scenarios", prompt: "Give me a bull case AND bear case for BTC over the next 3 months." },
  { emoji: "◆", title: "Nifty 50 at highs", prompt: "Nifty 50 is at record highs — should I be worried?" },
  { emoji: "◆", title: "Gold / Dollar dynamics", prompt: "Explain the gold/dollar relationship right now." },
];

export default function Chat() {
  const { user } = useAuth();
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState("");
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [modelId, setModelId] = useState("");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { streaming, send, cancel } = useChatStream();

  useEffect(() => {
    api.get("/chat/models").then(({ data }) => {
      setModels(data.models); setDefaultModel(data.default); setModelId(data.default);
    });
  }, []);

  const loadConvs = async (q = "") => {
    if (!user) return;
    const { data } = await api.get("/chat/conversations", { params: q ? { q } : {} });
    setConvs(data.items);
  };
  useEffect(() => { if (user) loadConvs(); }, [user]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, streaming]);

  const openConv = async (id) => {
    setActiveId(id); setMessages([]);
    try {
      const { data } = await api.get(`/chat/conversations/${id}`);
      setMessages(data.messages || []); setModelId(data.model || defaultModel);
    } catch { toast.error("Failed to load conversation"); }
  };

  const newConv = () => { setActiveId(null); setMessages([]); setInput(""); inputRef.current?.focus(); };

  const submit = async (text = input) => {
    if (!text.trim() || streaming) return;
    const guest = !user;
    const userMsg = { role: "user", content: text, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", pending: true }]);
    setInput("");
    let newConvId = activeId;
    await send({
      conv_id: activeId, message: text, model: modelId, guest,
      onMeta: (ev) => { if (ev.conv_id && ev.conv_id !== activeId) { newConvId = ev.conv_id; setActiveId(ev.conv_id); } },
      onDelta: (chunk) => {
        setMessages((m) => {
          const arr = m.slice(); const last = arr[arr.length - 1];
          if (last && last.role === "assistant") arr[arr.length - 1] = { ...last, content: (last.content || "") + chunk };
          return arr;
        });
      },
      onDone: () => {
        setMessages((m) => m.map((x, i) => i === m.length - 1 ? { ...x, pending: false } : x));
        if (user) loadConvs(query);
      },
      onError: (err) => { toast.error(err || "Stream error"); setMessages((m) => m.filter((x) => !x.pending)); },
    });
    if (!activeId && newConvId) setActiveId(newConvId);
  };

  const doRename = async (id) => {
    const t = renameValue.trim();
    if (!t) { setRenaming(null); return; }
    try { await api.patch(`/chat/conversations/${id}`, { title: t }); setRenaming(null); setRenameValue(""); loadConvs(query); }
    catch { toast.error("Rename failed"); }
  };

  const deleteConv = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try { await api.delete(`/chat/conversations/${id}`); if (activeId === id) newConv(); loadConvs(query); }
    catch { toast.error("Delete failed"); }
  };

  const changeModel = async (m) => {
    setModelId(m);
    if (activeId && user) { try { await api.patch(`/chat/conversations/${activeId}`, { model: m }); } catch { /* silent */ } }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px,1fr] h-[calc(100vh-6.5rem)]" data-testid="chat-page">
      {/* Sidebar */}
      <aside className="border-r border-[color:var(--border-default)] flex flex-col bg-[color:var(--bg-surface)]" data-testid="chat-sidebar">
        <div className="p-3 border-b border-[color:var(--border-subtle)] space-y-2">
          <button data-testid="new-chat-btn" onClick={newConv} className="btn btn-primary w-full">
            <Plus size={16} /> New Conversation
          </button>
          {user && (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                data-testid="conv-search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); loadConvs(e.target.value); }}
                placeholder="Search…"
                className="input !pl-9 !py-2 !text-sm"
              />
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {!user ? (
            <div className="p-3 text-xs text-[color:var(--text-tertiary)] leading-relaxed">
              <span className="chip chip-gold mb-2">GUEST</span>
              <p className="mt-2">Conversation is ephemeral. Sign in to save history, search, and rename.</p>
            </div>
          ) : convs.length === 0 ? (
            <div className="p-3 text-xs text-[color:var(--text-tertiary)]">No conversations yet.</div>
          ) : (
            convs.map((c) => (
              <div
                key={c.id}
                data-testid={`conv-item-${c.id}`}
                className={`group flex items-center gap-2 px-2.5 py-2.5 rounded-lg cursor-pointer mb-0.5 transition-colors ${activeId === c.id ? "bg-[color:var(--bg-hover)]" : "hover:bg-[color:var(--bg-elevated)]"}`}
                onClick={() => openConv(c.id)}
              >
                <MessageSquare size={14} className="text-[color:var(--text-muted)] shrink-0" />
                {renaming === c.id ? (
                  <input
                    autoFocus value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") doRename(c.id); if (e.key === "Escape") setRenaming(null); }}
                    onBlur={() => doRename(c.id)} onClick={(e) => e.stopPropagation()}
                    className="input !py-1 !text-sm flex-1"
                    data-testid={`rename-input-${c.id}`}
                  />
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[color:var(--text-primary)] truncate">{c.title}</div>
                      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{c.model?.replace("claude-", "").slice(0, 22)}</div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`rename-btn-${c.id}`} onClick={(e) => { e.stopPropagation(); setRenaming(c.id); setRenameValue(c.title); }} className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><Pencil size={12} /></button>
                      <button data-testid={`delete-btn-${c.id}`} onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }} className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--bear)]"><Trash2 size={12} /></button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main chat area */}
      <section className="flex flex-col bg-[color:var(--bg-base)] min-w-0">
        <div className="border-b border-[color:var(--border-subtle)] px-6 py-4 flex items-center justify-between gap-3 glass-strong">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--grad-purple)" }}>
              <Bot size={20} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <div className="section-eyebrow">◆ Multi-Agent Institutional Intelligence</div>
              <div className="text-xl font-semibold text-[color:var(--text-primary)]">Talk to the Trading Floor</div>
            </div>
          </div>
          <select
            data-testid="model-select"
            value={modelId}
            onChange={(e) => changeModel(e.target.value)}
            className="input !py-2 !text-sm !w-auto font-mono-data"
          >
            {models.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
          </select>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-8" data-testid="messages-scroll">
          {messages.length === 0 ? (
            <div className="max-w-3xl mx-auto text-center py-12">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: "var(--grad-purple)", boxShadow: "var(--shadow-glow-purple)" }}>
                <Sparkles size={28} color="white" strokeWidth={2.4} />
              </div>
              <h2 className="text-4xl font-serif-elite text-[color:var(--text-primary)] mb-3">
                <span className="gradient-text-purple">Ask anything</span> about the markets.
              </h2>
              <p className="text-base text-[color:var(--text-secondary)] max-w-xl mx-auto">
                Live prices for NIFTY, S&P 500, BTC, and Gold are auto-injected into every conversation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-10 text-left">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p.title}
                    data-testid={`starter-${p.prompt.slice(0, 12)}`}
                    onClick={() => submit(p.prompt)}
                    className="card p-4 hover:border-[color:var(--accent)] text-left group transition-all"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="gradient-text-gold text-lg font-bold">{p.emoji}</span>
                      <span className="text-sm font-semibold text-[color:var(--text-primary)]">{p.title}</span>
                    </div>
                    <div className="text-xs text-[color:var(--text-tertiary)] leading-relaxed">{p.prompt}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((m, i) => (
                <MessageBubble key={i} message={m} idx={i} />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-[color:var(--border-subtle)] p-4 md:p-6 glass-strong">
          <div className="max-w-3xl mx-auto">
            <div className="flex gap-3 items-end card !p-2">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder={user ? "Message the trading floor…" : "Guest mode — sign in to save. Message the floor…"}
                rows={2}
                className="flex-1 bg-transparent px-3 py-2 text-base text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] resize-none focus:outline-none"
              />
              {streaming ? (
                <button data-testid="stop-btn" onClick={cancel} className="btn m-1" style={{ background: "var(--bear)", color: "white" }}>
                  <Square size={14} /> Stop
                </button>
              ) : (
                <button data-testid="send-btn" onClick={() => submit()} disabled={!input.trim()} className="btn btn-primary m-1">
                  <Send size={14} /> Send
                </button>
              )}
            </div>
            <div className="text-[11px] text-[color:var(--text-muted)] mt-2 text-center font-mono-data">
              ⇧+ENTER for newline · Not financial advice · Probabilistic outputs
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function MessageBubble({ message, idx }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""} animate-fade-up`} data-testid={`msg-${message.role}-${idx}`}>
      <div className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center text-sm font-semibold ${isUser ? "bg-[color:var(--bg-elevated)] text-[color:var(--text-primary)] border border-[color:var(--border-default)]" : ""}`}
        style={isUser ? undefined : { background: "var(--grad-purple)", color: "white" }}>
        {isUser ? "You" : <Bot size={18} />}
      </div>
      <div className={`flex-1 max-w-[85%] ${isUser ? "text-right" : ""}`}>
        <div className={`text-xs mb-1.5 font-mono-data tracking-wider ${isUser ? "text-[color:var(--text-muted)]" : "gradient-text-gold font-semibold"}`}>
          {isUser ? "YOU" : "◆ TRADING FLOOR AI"}
        </div>
        <div className={`inline-block max-w-full text-left rounded-2xl px-5 py-4 ${
          isUser
            ? "bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] text-[color:var(--text-primary)]"
            : "card"
        }`}>
          {isUser ? (
            <div className="whitespace-pre-wrap text-base leading-relaxed">{message.content}</div>
          ) : (
            <div className="markdown-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
              >{message.content || (message.pending ? "…" : "")}</ReactMarkdown>
              {message.pending && <span className="inline-block w-1.5 h-4 bg-[color:var(--accent)] ml-1 animate-pulse align-middle" />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
